import { ThemeProvider } from "@mui/material/styles";
import React, { lazy, Suspense, useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import { Redirect, Route, Switch, useLocation } from "react-router-dom";
import { componentLoader, setDocumentFavicon } from "utils/utils";
import useAppStore from "stores/AppStore";
import useUserStore from "stores/UserStore";
import useUiStore, { isMobileWidth } from "stores/UiStore";
import useCustomConfig from "hooks/useConfig";
import {
  getAdminStatus,
  getPublisherStatus,
  getStatisticsAccess,
  checkMaintenanceMode,
} from "api/adminUtils";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./ToastStyles.css"; // Custom mobile-friendly toast styles
import MaintenanceDialog from "components/Dialogs/MaintenanceDialog";
import { embed } from "router/urlQueryFields";
import LoadingSpinner from "components/Common/LoadingSpinner";
import ProtectedRoute from "components/Common/ProtectedRoute";
import SuitabilityRoute from "./SuitabilityRoute";
import Analytics from "utils/analytics";
import {
  DEFAULT_THEME,
  ROUTES,
  LOADING_MESSAGES,
  TOAST_CONFIG_DESKTOP,
  TOAST_CONFIG_MOBILE,
} from "./AppConfig";
import { createAppTheme } from "theme/createAppTheme";
import { Dashboard } from "./dashboardLoader";

// Lazy-loaded components for code splitting and performance optimization.
// Dashboard comes from ./dashboardLoader, which index.js also prefetches.
const LegalDisclaimer = lazy(() =>
  componentLoader(() => import("components/Disclaimers/Legal")),
);
const PrivacyDisclaimer = lazy(() =>
  componentLoader(() => import("components/Disclaimers/Privacy")),
);
const Login = lazy(() =>
  componentLoader(() => import("components/Login/Login")),
);
const AdminPage = lazy(() =>
  componentLoader(() => import("components/Admin/Admin")),
);
const PublishPage = lazy(() =>
  componentLoader(() => import("components/Publish/Publish")),
);
const UsageDashboardPage = lazy(() =>
  componentLoader(() => import("components/UsageDashboard/UsageDashboard")),
);
const ShortUrlRedirect = lazy(() =>
  componentLoader(() => import("../../router/ShortUrlRedirect")),
);
const MultiCriteriaMapping = lazy(() =>
  componentLoader(
    () => import("components/MultiCriteria/MultiCriteriaMapping"),
  ),
);

// Analytics instance for tracking customization usage
const analytics = new Analytics();

/**
 * App Component - Root application component
 *
 * Responsibilities:
 * - Application routing and navigation
 * - Theme configuration and provider setup
 * - Authentication and authorization flow
 * - Maintenance mode management
 * - Global state initialization
 * - Responsive layout detection
 *
 * @returns {JSX.Element} The root application component
 */
function App() {
  const isSignedIn = useUserStore((state) => state.authenticated);
  const loadAvailableLanguages = useAppStore(
    (state) => state.loadAvailableLanguages,
  );
  const isMobile = useUiStore((state) => state.isMobile);
  const setIsMobile = useUiStore((state) => state.setIsMobile);
  const config = useCustomConfig();

  const location = useLocation();

  // Track previous authentication state for login success detection
  const prevIsSignedIn = useRef(undefined);

  // Maintenance mode state management
  // - isEnabled: Whether to display the maintenance dialog to users
  // - shouldBlock: Whether users can close the dialog (true = blocking mode)
  // - loading: Whether we're still fetching maintenance status from server
  const [maintenanceMode, setMaintenanceMode] = useState({
    isEnabled: false,
    shouldBlock: false,
    loading: true,
  });

  // Extract theme colors from config or use defaults
  const mainColor = config?.ui?.primaryColor ?? DEFAULT_THEME.primaryColor;
  const secondaryColor =
    config?.ui?.secondaryColor ?? DEFAULT_THEME.secondaryColor;

  // Initialize app on mount: load available languages and log startup
  // analytics. Mount-only on purpose — this was previously keyed on
  // location.search, so every URL-param write (map pan, layer toggle, ...)
  // re-fired an RTDB read and a spurious app_loaded event.
  useEffect(() => {
    loadAvailableLanguages();

    // Track app initialization
    try {
      // Set user properties for GA4 tracking
      analytics.setUserProperties();

      analytics.logCustomizationEvent("app_loaded");

      // Track if user is already authenticated
      if (isSignedIn === true) {
        analytics.logCustomizationEvent("already_authenticated");
      }
    } catch (error) {
      console.error("Error logging customization events:", error);
    }
  }, []);

  // Apply the customization favicon as soon as config is available, on every
  // entry path (login, embed, authenticated refresh). Previously this only ran
  // on the login screen, so embedded/auto-authenticated customizations kept the
  // generic default favicon.
  useEffect(() => {
    if (config?.ui?.favicon) {
      setDocumentFavicon(config.ui.favicon);
    }
  }, [config]);

  // Check maintenance mode on app load
  // Fetches maintenance status from server when config is available
  // Falls back to disabled state if there's an error
  useEffect(() => {
    const checkMaintenance = async () => {
      try {
        const maintenanceStatus = await checkMaintenanceMode();
        setMaintenanceMode({
          isEnabled: maintenanceStatus?.maintenanceMode?.isEnabled || false,
          shouldBlock: maintenanceStatus?.maintenanceMode?.shouldBlock || false,
          loading: false,
        });
      } catch (error) {
        // Fails open by design; a transient callable failure on a flaky
        // connection is not an app error (Sentry EARTHMAP-FRONTEND-2SF).
        console.warn("Error checking maintenance mode:", error);
        // On error, disable maintenance mode and allow app access
        setMaintenanceMode({
          isEnabled: false,
          shouldBlock: false,
          loading: false,
        });
      }
    };

    if (config !== null) {
      checkMaintenance();
    }
  }, [config]);

  // Track login success when user authenticates
  // Watches for isSignedIn changing from false/undefined to true
  useEffect(() => {
    try {
      // Check if user just logged in (transition from false/undefined to true)
      if (isSignedIn === true && prevIsSignedIn.current !== true) {
        // Only fire login_success if this is a new login (not already authenticated on mount)
        if (prevIsSignedIn.current === false) {
          analytics.logCustomizationEvent("login_success");
        }
      }
      // Update previous value
      prevIsSignedIn.current = isSignedIn;
    } catch (error) {
      console.error("Error logging login success event:", error);
    }
  }, [isSignedIn]);

  // Check if embed parameter is present for anonymous sign-in flow
  const anonymousSignIn = location.search.includes(embed);

  /**
   * rootRedirect - Determines the root path redirect based on config and auth status
   * - Shows empty div while config is loading
   * - Redirects to Dashboard if signed in
   * - Redirects to Login if not signed in (preserving URL parameters)
   */
  const rootRedirect = () => {
    if (config === null) {
      return <div />;
    }
    if (isSignedIn) {
      return <Dashboard />;
    }

    // Don't redirect if we're checking auth state - show loading instead
    // This prevents losing URL parameters during auth check
    if (isSignedIn === undefined) {
      return <div>Loading...</div>;
    }

    // Preserve URL parameters when redirecting to login
    return (
      <Redirect
        to={{
          pathname: ROUTES.LOGIN,
          search: location.search,
        }}
      />
    );
  };

  /**
   * renderMultiCriteriaRoute - Conditionally renders multicriteria mapping feature
   * Only accessible when the multicriteria feature flag (`suitabilityMapping`) is enabled
   * Uses a render function (not a component) to avoid remounting when App re-renders
   * SuitabilityRoute waits for the session to be restored before deciding.
   */
  const renderMultiCriteriaRoute = () => (
    <SuitabilityRoute config={config} component={MultiCriteriaMapping} />
  );

  // Material UI Theme - Memoized to prevent unnecessary re-renders
  const theme = React.useMemo(
    () => createAppTheme(mainColor, secondaryColor),
    [mainColor, secondaryColor],
  );

  /**
   * Global Window Resize Handler
   * Detects viewport size changes and updates the ui store with mobile/desktop status
   * Breakpoint: below Material-UI 'md' (900px), as CSS down("md")
   */
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(
        isMobileWidth(window.innerWidth, theme.breakpoints.values.md),
      );
    };

    // Set initial value
    handleResize();

    // Listen for resize events
    window.addEventListener("resize", handleResize);

    // Cleanup listener on unmount
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [setIsMobile, theme.breakpoints.values.md]);

  // Show loading screen while config is being loaded
  if (config === null) {
    return (
      <LoadingSpinner
        fullScreen
        messageId="app_loadingSite"
        backgroundColor={DEFAULT_THEME.primaryColor}
        textColor={DEFAULT_THEME.secondaryColor}
        logoSrc="/images/earth-map-logo-white-one-line.png"
      />
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <ToastContainer
        {...(isMobile ? TOAST_CONFIG_MOBILE : TOAST_CONFIG_DESKTOP)}
      />

      {/* Maintenance Mode Dialog */}
      <MaintenanceDialog
        open={maintenanceMode.isEnabled}
        onClose={() =>
          setMaintenanceMode({ ...maintenanceMode, isEnabled: false })
        }
        allowClose={!maintenanceMode.shouldBlock}
      />

      <Suspense fallback={<div />}>
        <Switch>
          <Route path={ROUTES.LEGAL} component={LegalDisclaimer} />
          <Route path={ROUTES.PRIVACY} component={PrivacyDisclaimer} />
          <Route path={ROUTES.SHORT_URL} component={ShortUrlRedirect} />
          <Route
            path={ROUTES.LOGIN}
            render={() => {
              return !isSignedIn && config !== null ? (
                <Login anonymous={anonymousSignIn} />
              ) : (
                <Redirect
                  to={{
                    pathname: ROUTES.ROOT,
                    search: location.search,
                  }}
                />
              );
            }}
          />
          <Route
            path={ROUTES.ADMIN}
            render={() => (
              <ProtectedRoute
                isSignedIn={isSignedIn}
                checkPermission={getAdminStatus}
                component={AdminPage}
                loadingMessage={LOADING_MESSAGES.ADMIN_ACCESS}
              />
            )}
          />
          <Route
            path={ROUTES.PUBLISH}
            render={() => (
              <ProtectedRoute
                isSignedIn={isSignedIn}
                checkPermission={getPublisherStatus}
                component={PublishPage}
                loadingMessage={LOADING_MESSAGES.PUBLISHER_ACCESS}
              />
            )}
          />
          <Route
            path={ROUTES.USAGE}
            render={() => (
              <ProtectedRoute
                isSignedIn={isSignedIn}
                checkPermission={getStatisticsAccess}
                component={UsageDashboardPage}
                loadingMessage={LOADING_MESSAGES.PUBLISHER_ACCESS}
              />
            )}
          />
          <Route path={ROUTES.SUITABILITY} render={renderMultiCriteriaRoute} />
          <Route path={ROUTES.ROOT} render={rootRedirect} />
        </Switch>
      </Suspense>
    </ThemeProvider>
  );
}

export default App;
